import React, { useState } from "react";
import {
  Box, Typography, TextField, Button, Paper, Table, TableHead,
  TableRow, TableCell, TableBody, Divider, Chip,
} from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getProfitLoss } from "./accountingApi";
import { useFinancialYear } from "./useFinancialYear";
import { AccountLedgerDialog, VoucherDialog } from "./LedgerDrilldown";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

function Section({ title, rows, total, color, onOpen }) {
  return (
    <>
      <TableRow sx={{ bgcolor: "action.selected" }}>
        <TableCell colSpan={3}><b>{title}</b></TableCell>
      </TableRow>
      {rows.map((r, i) => (
        <TableRow
          key={i}
          hover
          onClick={() => r.ledgerAccountId && onOpen(r)}
          sx={{ cursor: r.ledgerAccountId ? "pointer" : "default" }}
        >
          <TableCell sx={{ pl: 4 }}>{r.accountCode}</TableCell>
          <TableCell>{r.accountName}</TableCell>
          <TableCell align="right">₹ {fmt(r.amount)}</TableCell>
        </TableRow>
      ))}
      <TableRow>
        <TableCell colSpan={2} align="right" sx={{ fontWeight: 700 }}>Total {title}</TableCell>
        <TableCell align="right"><Chip label={`₹ ${fmt(total)}`} color={color} size="small" /></TableCell>
      </TableRow>
    </>
  );
}

export default function ProfitLoss() {
  const [from, setFrom]         = useState("");
  const [to, setTo]             = useState("");
  const [branch, setBranch]     = useState("");
  const [data, setData]         = useState(null);
  // Drill-down: the account whose entries are open, and the voucher opened from them.
  const [drillAccount, setDrillAccount] = useState(null);
  const [voucherId, setVoucherId]       = useState(null);

  useFinancialYear(setFrom, setTo);

  const load = async () => {
    if (!from || !to) return;
    const res = await getProfitLoss(from, to, branch);
    setData(res);
  };

  const exportXlsx = () => {
    if (!data) return;
    const rows = [
      ...data.revenue.map((r) => ({ Section: "Revenue", Code: r.accountCode, Name: r.accountName, Amount: r.amount })),
      { Section: "TOTAL REVENUE", Code: "", Name: "", Amount: data.totalRevenue },
      ...data.costOfGoodsSold.map((r) => ({ Section: "COGS", Code: r.accountCode, Name: r.accountName, Amount: r.amount })),
      { Section: "GROSS PROFIT", Code: "", Name: "", Amount: data.grossProfit },
      ...data.operatingExpenses.map((r) => ({ Section: "Operating Expenses", Code: r.accountCode, Name: r.accountName, Amount: r.amount })),
      { Section: "NET PROFIT", Code: "", Name: "", Amount: data.netProfit },
    ];
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "P&L");
    saveAs(new Blob([XLSX.write(wb, { type: "array", bookType: "xlsx" })]), `ProfitLoss_${from}_${to}.xlsx`);
  };

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>Profit & Loss Statement</Typography>
      {data && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          Click an account to see its entries, then a voucher to see the bill behind it.
        </Typography>
      )}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
          <TextField label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Branch" value={branch} onChange={(e) => setBranch(e.target.value)} sx={{ width: 140 }} placeholder="All" />
          <Button variant="contained" onClick={load}>Generate</Button>
          {data && <Button variant="outlined" onClick={exportXlsx}>Export Excel</Button>}
        </Box>
      </Paper>

      {data && (
        <Table size="small" component={Paper}>
          <TableHead sx={{ bgcolor: "primary.dark" }}>
            <TableRow>
              <TableCell sx={{ color: "white" }}>Code</TableCell>
              <TableCell sx={{ color: "white" }}>Account</TableCell>
              <TableCell align="right" sx={{ color: "white" }}>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <Section title="Revenue" rows={data.revenue} total={data.totalRevenue} color="success" onOpen={setDrillAccount} />
            <Section title="Cost of Goods Sold" rows={data.costOfGoodsSold} total={data.totalCogs} color="error" onOpen={setDrillAccount} />
            <TableRow sx={{ bgcolor: "primary.light" }}>
              <TableCell colSpan={2} align="right"><b>Gross Profit</b></TableCell>
              <TableCell align="right"><b>₹ {fmt(data.grossProfit)}</b></TableCell>
            </TableRow>
            <Section title="Operating Expenses" rows={data.operatingExpenses} total={data.totalOperatingExpenses} color="warning" onOpen={setDrillAccount} />
            <Divider component="tr" />
            <TableRow sx={{ bgcolor: data.netProfit >= 0 ? "success.light" : "error.light" }}>
              <TableCell colSpan={2} align="right">
                <Typography variant="h6">{data.netProfit >= 0 ? "Net Profit" : "Net Loss"}</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="h6">₹ {fmt(Math.abs(data.netProfit))}</Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}

      {data && (
        <>
          {/* P&L shows movement for the period only, so the ledger starts at zero and its
              closing is the account's net for the period. */}
          <AccountLedgerDialog
            account={drillAccount}
            from={data.from}
            to={data.to}
            branchCode={data.branchCode || ""}
            openingBalance={0}
            onClose={() => setDrillAccount(null)}
            onOpenVoucher={setVoucherId}
          />
          <VoucherDialog
            voucherHeaderId={voucherId}
            onClose={() => setVoucherId(null)}
            onOpenAccount={(acc) => { setVoucherId(null); setDrillAccount(acc); }}
          />
        </>
      )}
    </Box>
  );
}
